import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Stack } from '@/components/ui/layout';
import { Title, Text, Button, Anchor, Badge } from '@/components/ui/core';
import { TextInput, Textarea, Checkbox } from '@/components/ui/inputs';
import { Alert } from '@/components/ui/feedback';
import { IconTrash, IconArrowLeft, IconCheck, IconAlertCircle, IconMail, IconUser, IconPhone } from '@tabler/icons-react';
import { FadeIn, AnimatedFormWrapper } from '@/components/animations';
import { useHttpClient } from '@actschurch/ccms-sdk/react';
import { validators } from '@/utils/validation';
import ReCaptcha from '@/components/common/ReCaptcha';

export default function DataDeletionRequestPage() {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [details, setDetails] = useState('');
  const [confirmDeletion, setConfirmDeletion] = useState(false);
  const [website, setWebsite] = useState(''); // honeypot — must stay empty

  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ fullName?: string; email?: string; confirm?: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [reference, setReference] = useState('');
  const [apiError, setApiError] = useState('');

  const httpClient = useHttpClient();
  const siteKey = (import.meta as any).env?.VITE_RECAPTCHA_SITE_KEY as string | undefined;

  const validate = (): boolean => {
    const next: { fullName?: string; email?: string; confirm?: string } = {};
    const nameCheck = validators.required(fullName);
    if (nameCheck !== true) next.fullName = nameCheck;
    const emailCheck = validators.email(email);
    if (emailCheck !== true) next.email = emailCheck;
    if (!confirmDeletion) next.confirm = 'Please confirm your request to continue.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!validate()) return;

    // Require the bot check only when reCAPTCHA is actually configured.
    if (siteKey && !captchaToken) {
      setApiError('Please complete the bot check below before submitting.');
      return;
    }

    setIsLoading(true);
    try {
      const res: any = await httpClient.post('/users/api/data-deletion/request', {
        fullName,
        email,
        phoneNumber: phoneNumber || undefined,
        details: details || undefined,
        confirmDeletion,
        captchaToken,
        website, // honeypot
      });
      const payload = res?.data ?? res;
      const data = payload?.data ?? payload;
      setReference(data?.referenceCode ?? '');
      setSuccess(true);
    } catch (err: any) {
      setApiError(
        err?.response?.data?.message || err?.message || 'We could not submit your request. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <FadeIn delay={200}>
        <Stack align="center" gap="xs">
          <Badge variant="light" color="red" size="lg" leftSection={<IconTrash size={12} />}>
            Data & Privacy
          </Badge>
          <Title order={2} ta="center" mt="md">
            Request deletion of your information
          </Title>
          <Text c="dimmed" size="sm" ta="center">
            Ask Acts Christian Church to delete the personal information held about you in CCMS.
            We'll verify your request and contact you at the email you provide. Requests are reviewed
            by our team and are not actioned automatically.
          </Text>
        </Stack>
      </FadeIn>

      {/* Success */}
      {success && (
        <FadeIn>
          <Alert icon={<IconCheck size={16} />} title="Request received" color="green">
            <Stack gap="xs">
              <Text size="sm">
                Thank you. Your data deletion request has been received and our team will review it.
                We'll contact you at <strong>{email}</strong>.
              </Text>
              {reference && (
                <Text size="sm">
                  Your reference number is <strong>{reference}</strong> — please keep it for your records.
                </Text>
              )}
              <Anchor component={Link} to="/auth/login" size="sm">
                Back to login
              </Anchor>
            </Stack>
          </Alert>
        </FadeIn>
      )}

      {/* Error */}
      {apiError && (
        <FadeIn>
          <Alert icon={<IconAlertCircle size={16} />} title="Something went wrong" color="red">
            {apiError}
          </Alert>
        </FadeIn>
      )}

      {/* Form */}
      {!success && (
        <form onSubmit={handleSubmit}>
          <AnimatedFormWrapper animation="stagger" delay={300} staggerDelay={80}>
            <TextInput
              label="Full name"
              placeholder="Your full name"
              leftSection={<IconUser size={16} />}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              error={errors.fullName}
              required
              size="md"
              radius="md"
            />

            <TextInput
              label="Email address"
              placeholder="your@email.com"
              leftSection={<IconMail size={16} />}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              required
              size="md"
              radius="md"
              description="Use the email associated with your CCMS account so we can verify the request."
            />

            <TextInput
              label="Phone number (optional)"
              placeholder="e.g. +27 ..."
              leftSection={<IconPhone size={16} />}
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              size="md"
              radius="md"
            />

            <Textarea
              label="Additional details (optional)"
              placeholder="Anything that helps us locate and verify your information."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              minRows={3}
              autosize
              maxRows={6}
              size="md"
              radius="md"
            />

            {/* Honeypot — hidden from real users; bots tend to fill it. */}
            <div
              aria-hidden="true"
              style={{ position: 'absolute', left: '-9999px', top: 0, width: 1, height: 1, overflow: 'hidden' }}
            >
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            <Checkbox
              label="I confirm I am requesting that my personal information be deleted from CCMS, and that the details above are accurate."
              checked={confirmDeletion}
              onChange={(e) => setConfirmDeletion(e.currentTarget.checked)}
              error={errors.confirm}
            />

            <ReCaptcha onVerify={setCaptchaToken} />

            <Button type="submit" fullWidth size="md" radius="md" color="red" loading={isLoading} leftSection={<IconTrash size={16} />}>
              Submit deletion request
            </Button>
          </AnimatedFormWrapper>
        </form>
      )}

      {/* Footer links */}
      <FadeIn delay={700}>
        <Text ta="center" size="sm" c="dimmed">
          <Anchor component={Link} to="/auth/login" size="sm">
            <IconArrowLeft size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Back to login
          </Anchor>
          {' · '}
          <Anchor component={Link} to="/privacy" size="sm">
            Privacy Policy
          </Anchor>
        </Text>
      </FadeIn>
    </Stack>
  );
}
